import { Injectable } from '@angular/core';
import { State, Action, StateContext, Store } from '@ngxs/store';
import produce from 'immer';
import {
  CreateToDo,
  MakePayment,
  ResetOrderWizardSubmission,
  SetCollectionStatusIfNeeded,
  SubmitReorder,
} from './order-wizard-submission.actions';
import { defaultApiStoreData, MissingApiRequestDataError } from '../../utils';
import { ApiService, SentryService } from '../../../services';
import { OrdersState } from '../../order/orders.state';
import {
  buildCollectionStatusRequest,
  buildCreateToDoRequest,
  buildPaymentRequest,
  buildSubmitReorderRequest,
  createUserNote,
} from '../../../models';
import { AddressesState } from '../../addresses/addresses.state';
import { catchError, Observable, of, tap } from 'rxjs';
import { OrderWizardSubmissionStateModel } from './order-wizard-submission.model';
import { OrderWizardDataState } from '../order-wizard-data/order-wizard-data.state';
import { Selector } from '@ngxs/store';
import { SETS_RESERVOIRS_HCPCS_GROUP, TESTING_SUPPLIES_HCPCS_GROUP } from 'src/app/core/constants';
import { OrdersStateModel } from '../../order/orders.state';
import { PaymentMethodsState, PaymentMethodsStateModel } from '../../payment-methods/payment-methods.state';
import { ApiStoreData, hasUnknownOrInvalidData } from '../../utils';
import { OrderWizardDataStateModel, OrderWizardStatus } from '../order-wizard-data/order-wizard-data.model';
import { PatientState } from '../../patient/patient.state';
import { PatientInsurancesState } from '../../patient-insurances/patient-insurances.state';

const defaults = {
  makePayment: defaultApiStoreData,
  createToDo: defaultApiStoreData,
  setCollectionStatus: defaultApiStoreData,
  reorderSubmission: defaultApiStoreData,
};

@State<OrderWizardSubmissionStateModel>({
  name: 'orderWizardSubmission',
  defaults,
})
@Injectable({ providedIn: 'root' })
export class OrderWizardSubmissionState {
  constructor(private apiService: ApiService, private store: Store, private sentry: SentryService) {}

  handleError = this.sentry.handleError;
  handleSuccess = this.sentry.handleSuccess;

  @Selector([OrderWizardDataState, PaymentMethodsState])
  static submitToAgent(
    state: OrderWizardSubmissionStateModel,
    orderWizardState: OrderWizardDataStateModel,
    paymentMethodsState: PaymentMethodsStateModel
  ): OrderWizardStatus {
    let submitToAgent = true;
    const reorder = orderWizardState.reorder.data;

    const isAgentShipmentType = !!reorder && !reorder.isReorderConclusionShipment;
    const hasQuantityValidationIssues = !!reorder && reorder.items.some((i) => !!i.quantityValidationIssue);
    const quantitiesUserData = orderWizardState.userData.productsStep.quantities;
    const testingSuppliesItems = reorder?.items.filter((i) => i.hcpcsGroup === TESTING_SUPPLIES_HCPCS_GROUP) || [];

    const hasUserRemovedAllItems = !!quantitiesUserData && quantitiesUserData.every((qud) => qud.userQuantity === 0);
    const haveTestingSuppliesQuantitiesChanged =
      !!quantitiesUserData &&
      quantitiesUserData
        .filter((qud) => testingSuppliesItems.map((i) => i.uiId).includes(qud.itemUiId))
        .some((qud) => qud.userQuantity !== qud.initialQuantity);

    const hasUnknownOrInvalidDohRequirements = hasUnknownOrInvalidData(orderWizardState.daysOnHandRequired);
    const hasUnknownOrInvalidFrequenciesRequirements = hasUnknownOrInvalidData(orderWizardState.frequenciesRequired);
    const hasUnknownOrInvalidProviderVisitRequirements = hasUnknownOrInvalidData(
      orderWizardState.providerVisitVerification
    );
    const hasUnknownOrInvalidProviderVisitValidation =
      hasUnknownOrInvalidData(orderWizardState.lastProviderVisitValidation) ||
      hasUnknownOrInvalidData(orderWizardState.nextProviderVisitValidation);
    const hideSummaryStepBillingInfo = OrderWizardDataState.hideSummaryStepBillingInfo(orderWizardState).value;
    const skipCollectionForReconsent = OrderWizardDataState.skipCollectionForReconsent(orderWizardState);

    const copayData = orderWizardState.copay.data;
    const patientResponsiblityGreaterThanZero =
      copayData?.estimatedPatientResponsibility !== undefined && copayData?.estimatedPatientResponsibility > 0;
    const hasUnknownOrInvalidPaymentMethodsWithResponsibility =
      patientResponsiblityGreaterThanZero &&
      OrderWizardDataState.hasUnknownOrInvalidPaymentMethods(orderWizardState, paymentMethodsState);

    // TODO Remove this condition when we restore erroring out on payment failures
    // For now, when a payment failure occurs, we proceed with the order submitting to agent
    const hasPaymentFailed = !!state.makePayment.error;

    if (
      !isAgentShipmentType &&
      !haveTestingSuppliesQuantitiesChanged &&
      !hasUserRemovedAllItems &&
      !hasQuantityValidationIssues &&
      !hasUnknownOrInvalidDohRequirements &&
      !hasUnknownOrInvalidFrequenciesRequirements &&
      !hasUnknownOrInvalidProviderVisitRequirements &&
      !hasUnknownOrInvalidProviderVisitValidation &&
      (!hideSummaryStepBillingInfo || skipCollectionForReconsent) &&
      !hasUnknownOrInvalidPaymentMethodsWithResponsibility &&
      !hasPaymentFailed
    ) {
      submitToAgent = false;
    }

    return {
      value: submitToAgent,
      debugInfo: {
        isAgentShipmentType,
        haveTestingSuppliesQuantitiesChanged,
        hasUserRemovedAllItems,
        hasQuantityValidationIssues,
        hasUnknownOrInvalidDohRequirements,
        hasUnknownOrInvalidFrequenciesRequirements,
        hasUnknownOrInvalidProviderVisitRequirements,
        hasUnknownOrInvalidProviderVisitValidation,
        hideSummaryStepBillingInfo,
        skipCollectionForReconsent,
        hasUnknownOrInvalidPaymentMethodsWithResponsibility,
        hasPaymentFailed,
      },
    };
  }

  @Selector([OrderWizardDataState])
  static sendToDo(
    state: OrderWizardSubmissionStateModel,
    orderWizardState: OrderWizardDataStateModel
  ): OrderWizardStatus {
    let sendToDo = false;
    const hasUnknownOrInvalidReorderData = orderWizardState.reorder.isLoading || !!orderWizardState.reorder.error;
    const reorder = orderWizardState.reorder.data;
    const quantitiesUserData = orderWizardState.userData.productsStep.quantities;
    const isReorderConclusionShipmentType = !!reorder && reorder.isReorderConclusionShipment;

    const setsOrReservoirsNonMedicareItems =
      reorder?.items.filter((i) => i.hcpcsGroup === SETS_RESERVOIRS_HCPCS_GROUP && !i.isMedicareOrMedicareRepl) || [];

    const nonMedicareUiIds = new Set(setsOrReservoirsNonMedicareItems.map((item) => item.uiId));

    const haveNonMedicareSetsOrReservoirQuantitiesIncreased =
      Array.isArray(quantitiesUserData) &&
      quantitiesUserData.some(
        (quantityData) =>
          nonMedicareUiIds.has(quantityData.itemUiId) &&
          quantityData.userQuantity > (quantityData.maxQuantity ?? Number.MAX_SAFE_INTEGER)
      );

    sendToDo =
      !hasUnknownOrInvalidReorderData &&
      isReorderConclusionShipmentType &&
      haveNonMedicareSetsOrReservoirQuantitiesIncreased;

    return {
      value: sendToDo,
      debugInfo: {
        hasUnknownOrInvalidReorderData,
        isReorderConclusionShipmentType,
        haveNonMedicareSetsOrReservoirQuantitiesIncreased,
      },
    };
  }

  @Selector([OrderWizardDataState, OrdersState])
  static shouldSetCollectionStatus(
    state: OrderWizardSubmissionStateModel,
    orderWizardState: OrderWizardDataStateModel,
    ordersState: OrdersStateModel
  ): OrderWizardStatus {
    let shouldSetCollectionStatus = false;

    const reorder = orderWizardState.reorder.data;
    const patientOrders = OrdersState.allOrders(ordersState);
    const foundPatientOrder = patientOrders.find((o) => o.id === reorder?.id);
    const hasUnknownOrInvalidReorderData =
      orderWizardState.reorder.isLoading || !!orderWizardState.reorder.error || !foundPatientOrder;
    const isShipment = foundPatientOrder?.isShipment || false;
    const hideSummaryStepBillingInfo = OrderWizardDataState.hideSummaryStepBillingInfo(orderWizardState).value;

    shouldSetCollectionStatus = !hasUnknownOrInvalidReorderData && isShipment && !hideSummaryStepBillingInfo;

    return {
      value: shouldSetCollectionStatus,
      debugInfo: {
        hasUnknownOrInvalidReorderData,
        isShipment,
        hideSummaryStepBillingInfo,
      },
    };
  }

  @Selector()
  static submitReorder(state: OrderWizardSubmissionStateModel): ApiStoreData<null> {
    return state.reorderSubmission;
  }

  @Selector()
  static makePayment(state: OrderWizardSubmissionStateModel): ApiStoreData<null> {
    return state.makePayment;
  }

  @Action(SubmitReorder)
  submitReorder(ctx: StateContext<OrderWizardSubmissionStateModel>, action: SubmitReorder) {
    const errors: string[] = [];

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);
    const reorder = this.store.selectSnapshot(OrderWizardDataState.reorder);
    const computedTargetShipDate = this.store.selectSnapshot(OrderWizardDataState.computedTargetShipDate);
    const insuranceData = this.store.selectSnapshot(PatientInsurancesState.patientInsurance);
    const providers = this.store.selectSnapshot(OrderWizardDataState.providers);
    const providerUserData = this.store.selectSnapshot(OrderWizardDataState.providerUserData);
    const productsUserData = this.store.selectSnapshot(OrderWizardDataState.productsUserData);

    if (!patientId) errors.push('Missing patient id');

    if (!reorder) {
      errors.push('Missing reorder data');
    } else {
      if (!reorder.shipmentTypeId) errors.push('Missing reorder shipment type ID');
    }

    if (!computedTargetShipDate) errors.push('Missing computed target ship date');

    if (!insuranceData) errors.push('Missing insurance data');

    if (!providers) {
      errors.push('Missing provider data');
    } else {
      const selectedProviderId = providerUserData.selectedProviderId;
      if (!selectedProviderId) {
        errors.push('Missing selectedProviderId.');
      } else if (!providerUserData.newProviderFormValues && !providers.find((p) => p.id === selectedProviderId)) {
        errors.push('No provider has ID matching selectedProviderId.');
      }
    }

    const userDataQuantities = productsUserData.quantities;
    if (!!reorder) {
      const quantities = reorder.items.map((i) => ({
        original: i.quantity,
        changed: userDataQuantities?.find((i2) => i2.itemUiId === i.uiId)?.quantityToSubmit,
      }));

      if (quantities.some((i) => !i.original && i.original !== 0 && !i.changed && i.changed !== 0)) {
        errors.push('Missing both original quantity and quantity to submit on reorder item');
      }
    }

    const patientOrders = this.store.selectSnapshot(OrdersState.allOrders);
    const matchingOrder = patientOrders.find((o) => o.id === reorder!.id);

    if (!matchingOrder) {
      errors.push('Missing matching dashboard order upon order submission');
    }

    if (errors.length > 0) {
      const error = new MissingApiRequestDataError(errors);
      return this.handleError({ ctx, error, stateKey: 'reorderSubmission', resetData: false });
    }

    const sessionId = this.store.selectSnapshot(PatientState.sessionId);
    const patientAddresses = this.store.selectSnapshot(AddressesState.patientAddresses);
    const isOuShipment = this.store.selectSnapshot(OrderWizardDataState.isOuShipment);
    const copay = this.store.selectSnapshot(OrderWizardDataState.copay);

    const orderSubmissionUserNote = createUserNote(
      this.store.selectSnapshot(OrderWizardDataState.canAutoComputeTargetShipDate),
      this.store.selectSnapshot(OrderWizardDataState.doNotFetchCopay),
      this.store.selectSnapshot(OrderWizardDataState.hideSummaryStepBillingInfo),
      this.store.selectSnapshot(OrderWizardDataState.skipBillingStep),
      this.store.selectSnapshot(OrderWizardSubmissionState.submitToAgent),
      copay,
      this.store.selectSnapshot(OrderWizardDataState.appliedDeductibleOverridesUserData),
      action.userWillPayLater
    );

    const body = buildSubmitReorderRequest(
      patientId!,
      reorder!,
      computedTargetShipDate!,
      matchingOrder!,
      this.store.selectSnapshot(OrderWizardDataState.datesOfService),
      this.store.selectSnapshot(OrderWizardDataState.providerVisitVerification),
      providerUserData,
      providers!,
      productsUserData,
      insuranceData!,
      this.store.selectSnapshot(OrderWizardDataState.insuranceUserData),
      this.store.selectSnapshot(OrderWizardDataState.daysOnHandRequired),
      this.store.selectSnapshot(OrderWizardSubmissionState.submitToAgent),
      this.store.selectSnapshot(OrderWizardDataState.contactsUserData),
      patientAddresses,
      isOuShipment,
      orderSubmissionUserNote,
      sessionId || undefined
    );

    ctx.setState(
      produce((draft) => {
        draft.reorderSubmission.isLoading = true;
      })
    );

    return (
      matchingOrder!.isShipment
        ? this.apiService.submitShipment(body)
        : this.store.selectSnapshot(OrderWizardSubmissionState.submitToAgent).value
        ? this.apiService.submitReorderForAgent(body)
        : this.apiService.submitReorder(body)
    ).pipe(
      tap({
        next: () => {
          this.handleSuccess({ ctx, stateKey: 'reorderSubmission' });
        },
      }),
      catchError(
        (error: Error): Observable<any> =>
          this.handleError({ ctx, error, stateKey: 'reorderSubmission', resetData: false })
      )
    );
  }

  @Action(MakePayment)
  makePayment(ctx: StateContext<OrderWizardSubmissionStateModel>) {
    const state = ctx.getState();
    const errors: string[] = [];

    // Avoid making duplicate payments in case of user re-attempting order submission:
    if (state.makePayment.isFinished && !state.makePayment.error) {
      return of(null);
    }

    const copayData = this.store.selectSnapshot(OrderWizardDataState.copay);
    if (!copayData) errors.push('Missing copay data');

    const selectedPaymentMethod = this.store.selectSnapshot(OrderWizardDataState.selectedPaymentMethod);
    if (!selectedPaymentMethod) errors.push('Missing selected payment method');

    if (errors.length > 0) {
      const error = new MissingApiRequestDataError(errors);
      return this.handleError({ ctx, error, stateKey: 'makePayment', resetData: false });
    }

    const body = buildPaymentRequest({
      paymentMethodId: selectedPaymentMethod!.id,
      paymentAmount: copayData!.estimatedPatientResponsibility,
    });
    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: 'makePayment' });
    }

    ctx.setState(
      produce((draft) => {
        draft.makePayment.isLoading = true;
      })
    );

    return this.apiService.makePayment(body, patientId).pipe(
      tap({
        next: () => {
          this.handleSuccess({ ctx, stateKey: 'makePayment' });
        },
      }),
      catchError(
        (error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'makePayment', resetData: false })
      )
    );
  }

  @Action(CreateToDo)
  createToDo(ctx: StateContext<OrderWizardSubmissionStateModel>) {
    const state = ctx.getState();

    // PLPWP-2195 - FIxed Sentry error
    const productFrequencies = this.store.selectSnapshot(OrderWizardDataState.productFrequenciesUserData);
    const requiredFrequency = this.store.selectSnapshot(OrderWizardDataState.frequenciesRequired);

    if (requiredFrequency?.pumpSiteChgFreqRequired && !productFrequencies?.pumpSiteChgFreq) {
      const error = new MissingApiRequestDataError(['Missing pump site change frequency.']);
      return this.handleError({ ctx, error, stateKey: 'createToDo', resetData: false });
    }

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: 'createToDo' });
    }

    const body = buildCreateToDoRequest(patientId, productFrequencies!.pumpSiteChgFreq!);

    ctx.setState(
      produce((draft) => {
        draft.createToDo.isLoading = true;
      })
    );

    return this.apiService.createToDo(body).pipe(
      tap({
        next: () => {
          this.handleSuccess({ ctx, stateKey: 'createToDo' });
        },
      }),
      catchError(
        (error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'createToDo', resetData: false })
      )
    );
  }

  @Action(SetCollectionStatusIfNeeded)
  setCollecionStatusIfNeeded(ctx: StateContext<OrderWizardSubmissionStateModel>, action: SetCollectionStatusIfNeeded) {
    const state = ctx.getState();

    if (!this.store.selectSnapshot(OrderWizardSubmissionState.shouldSetCollectionStatus).value) {
      return of(null);
    }

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);
    const reorderId = this.store.selectSnapshot(OrderWizardDataState.reorder)?.id;
    const patientCopay = this.store.selectSnapshot(OrderWizardDataState.copay);

    let errorStr = null;

    if (!patientId) {
      errorStr = 'Missing patient id';
    } else if (!reorderId!) {
      errorStr = 'Missing reorder id';
    } else if (!patientCopay) {
      errorStr = 'Missing copay data';
    }

    if (errorStr) {
      const error = new MissingApiRequestDataError([errorStr]);
      return this.handleError({ ctx, error, stateKey: 'setCollectionStatus' });
    }

    const body = buildCollectionStatusRequest(patientId!, reorderId!, patientCopay!, action.isPaymentMade);

    ctx.setState(
      produce((draft) => {
        draft.setCollectionStatus.isLoading = true;
      })
    );

    return this.apiService.setShipmentCollectionStatus(patientId!, reorderId!, body).pipe(
      tap({
        next: () => {
          this.handleSuccess({ ctx, stateKey: 'setCollectionStatus' });
        },
      }),
      catchError(
        (error: Error): Observable<any> =>
          this.handleError({ ctx, error, stateKey: 'setCollectionStatus', resetData: false })
      )
    );
  }

  @Action(ResetOrderWizardSubmission)
  reset(ctx: StateContext<OrderWizardSubmissionStateModel>) {
    ctx.setState(
      produce((draft) => {
        Object.assign(draft, defaults);
      })
    );
  }
}
